/**
 * Composant de visualisation du Jour Critique
 * Affiche une frise temporelle montrant la progression vers le jour critique (marge = 0)
 */

import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { addBusinessDays } from '@/lib/heures-utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Info } from 'lucide-react';

interface CriticalDayVisualizationProps {
  joursEcoules: number;
  jourCritique: number | null;
  joursRestantsRentables: number | null;
  dateDebutChantier: string | null;
  dateFinPrevue: string | null;
  dureeEstimee: number | null;
  joursSuspensionTotal: number;
  joursRetard?: number; // Jours de retard déclarés (optionnel)
  criticalDelayDays?: number | null; // Nombre de jours de retard avant déficit (calculé par simulation)
  // Métriques pour calcul local du criticalDelayDays si non fourni
  margeFinale?: number | null;
  coutMoyenJournalier?: number | null;
  dateFinDerniereAffectation?: string | null; // MAX(date_fin) des affectations — date réelle de fin des travaux
}

/**
 * Formate une date en format court français (ex: "15 fév")
 */
function formatDateShort(date: Date | null): string {
  if (!date) return '—';
  return format(date, 'd MMM', { locale: fr });
}

export function CriticalDayVisualization({
  joursEcoules,
  jourCritique,
  joursRestantsRentables,
  dateDebutChantier,
  dateFinPrevue,
  dureeEstimee,
  joursSuspensionTotal,
  joursRetard,
  criticalDelayDays,
  margeFinale,
  coutMoyenJournalier,
  dateFinDerniereAffectation,
}: CriticalDayVisualizationProps) {
  // Calculs mémoïsés
  const calculations = useMemo(() => {
    // Jours écoulés effectifs (sans suspensions)
    const joursEffectifs = Math.max(0, joursEcoules - joursSuspensionTotal);

    // === CALCUL LOCAL DE criticalDelayDays SI NON FOURNI ===
    // Ceci garantit un calcul correct même si le callback de DelayPenaltySimulator n'a pas encore été appelé
    let criticalDelayDaysEffectif = criticalDelayDays;
    if ((criticalDelayDaysEffectif === null || criticalDelayDaysEffectif === undefined)
        && margeFinale !== null && margeFinale !== undefined
        && coutMoyenJournalier && coutMoyenJournalier > 0) {
      // Nombre de jours de retard supplémentaires avant déficit
      // Formule: marge disponible / coût journalier = jours avant breakeven
      criticalDelayDaysEffectif = Math.floor(margeFinale / coutMoyenJournalier);
    }

    // Calcul du jour critique effectif :
    // Si criticalDelayDaysEffectif est fourni (calculé par simulation ou localement), l'utiliser
    // Sinon, fallback sur jourCritique de metrics
    const jourCritiqueEffectif = (criticalDelayDaysEffectif !== null && criticalDelayDaysEffectif !== undefined && dureeEstimee)
      ? dureeEstimee + criticalDelayDaysEffectif
      : jourCritique;

    // Si pas de jour critique, on ne peut pas afficher la visualisation
    if (!jourCritiqueEffectif || jourCritiqueEffectif <= 0) {
      return {
        joursEffectifs,
        progress: 0,
        dateJourCritique: null,
        joursDeMargeRestants: null,
        finPrevueProgress: null,
        canDisplay: false,
        tickMarks: [],
        jourCritiqueEffectif: 0,
      };
    }

    // Position sur la barre (0-100%), capped à 100%
    const progress = Math.min((joursEffectifs / jourCritiqueEffectif) * 100, 100);

    // Calcul de la date du jour critique :
    // 1. Prendre la date de fin de la dernière affectation (MAX(date_fin) de toutes les affectations)
    // 2. Ajouter les jours rentables restants (marge / coût journalier) en jours ouvrés
    // Exemple : fin affectation = lundi 2 fév, 2 jours rentables → jour critique = mercredi 4 fév

    // Date de fin des travaux = dernière affectation réelle, fallback sur dateFinPrevue
    const dateFinTravaux = dateFinDerniereAffectation
      ? new Date(dateFinDerniereAffectation)
      : (dateFinPrevue ? new Date(dateFinPrevue) : null);

    // Jours rentables après fin des travaux = marge finale / coût journalier équipe
    // marge_finale inclut déjà les coûts de retard, donc pas besoin de soustraire joursRetard
    const joursDeMargeRestantsCalc = (margeFinale !== null && margeFinale !== undefined
      && coutMoyenJournalier && coutMoyenJournalier > 0)
      ? Math.max(0, Math.floor(margeFinale / coutMoyenJournalier))
      : (joursRestantsRentables ?? 0);

    // Date du jour critique = fin des travaux + jours rentables (en jours ouvrés)
    const dateJourCritique = dateFinTravaux
      ? addBusinessDays(dateFinTravaux, joursDeMargeRestantsCalc)
      : null;

    // Position de la date de fin prévue sur la barre (si disponible)
    let finPrevueProgress: number | null = null;
    if (dureeEstimee && jourCritiqueEffectif > 0) {
      finPrevueProgress = Math.min((dureeEstimee / jourCritiqueEffectif) * 100, 100);
    }

    // Position de la nouvelle fin (avec retard) sur la barre - seulement si retard > 0
    let nouvelleFinProgress: number | null = null;
    if (joursRetard && joursRetard > 0 && dureeEstimee && jourCritiqueEffectif > 0) {
      nouvelleFinProgress = Math.min(((dureeEstimee + joursRetard) / jourCritiqueEffectif) * 100, 100);
    }

    // Calcul des graduations dynamiques selon le nombre total de jours
    const tickMarks: { day: number; position: number; showLabel: boolean }[] = [];

    // Déterminer l'intervalle des graduations selon le nombre de jours
    let interval: number;
    if (jourCritiqueEffectif <= 10) {
      interval = 1; // Chaque jour
    } else if (jourCritiqueEffectif <= 30) {
      interval = 5; // Tous les 5 jours
    } else if (jourCritiqueEffectif <= 60) {
      interval = 10; // Tous les 10 jours
    } else if (jourCritiqueEffectif <= 120) {
      interval = 20; // Tous les 20 jours
    } else {
      interval = 30; // Tous les 30 jours
    }

    // Générer les graduations (exclure 0 et le jour critique lui-même)
    for (let day = interval; day < jourCritiqueEffectif; day += interval) {
      const position = (day / jourCritiqueEffectif) * 100;
      tickMarks.push({
        day,
        position,
        showLabel: true, // Afficher le label pour toutes les graduations
      });
    }

    return {
      joursEffectifs,
      progress,
      dateJourCritique,
      joursDeMargeRestants: joursDeMargeRestantsCalc,
      finPrevueProgress,
      nouvelleFinProgress,
      canDisplay: true,
      tickMarks,
      jourCritiqueEffectif,
    };
  }, [joursEcoules, jourCritique, joursRestantsRentables, dateDebutChantier, dateFinPrevue, dureeEstimee, joursSuspensionTotal, joursRetard, criticalDelayDays, margeFinale, coutMoyenJournalier, dateFinDerniereAffectation]);

  // Si pas de données suffisantes, afficher un message
  if (!calculations.canDisplay) {
    return (
      <Card className="card-premium">
        <CardContent className="pt-6 pb-6">
          <h3 className="text-xl font-bold text-center text-foreground mb-4">
            Visualisation du Jour Critique
          </h3>
          <p className="text-center text-muted-foreground text-sm">
            Affectez une équipe au chantier pour visualiser le jour critique.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Déterminer la couleur du texte des jours de marge
  const getMargeColor = (jours: number | null) => {
    if (jours === null) return 'text-foreground';
    if (jours <= 2) return 'text-red-500';
    if (jours <= 5) return 'text-orange-500';
    if (jours <= 10) return 'text-yellow-500';
    return 'text-green-500';
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Card className="card-premium">
        <CardContent className="pt-6 pb-6">
          {/* Titre avec infobulle */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <h3 className="text-xl font-bold text-center text-foreground">
              Visualisation du Jour Critique
            </h3>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="text-muted-foreground hover:text-foreground transition-colors">
                  <Info className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p className="text-sm">
                  Le <strong>Jour Critique</strong> est le jour où votre marge devient nulle (breakeven).
                  Cette visualisation vous aide à anticiper et ajuster votre gestion de chantier.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Frise / Progress bar */}
          <div className="relative px-4">

          {/* Container de la barre avec espace pour les labels */}
          <div className="relative">
            {/* Barre principale */}
            <div className="relative h-2.5 rounded-full overflow-hidden bg-slate-600/50">
              {/* Barre de progression avec dégradé */}
              <div
                className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
                style={{
                  width: `${calculations.progress}%`,
                  background: 'linear-gradient(to right, #22c55e 0%, #eab308 50%, #f97316 80%, #ef4444 100%)',
                }}
              />

              {/* Marqueur "Date de fin prévue" (si disponible) */}
              {calculations.finPrevueProgress !== null && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 cursor-help transition-all duration-500"
                      style={{ left: `${calculations.finPrevueProgress}%` }}
                    >
                      <div className="w-1.5 h-5 bg-green-500 rounded-sm shadow-lg" />
                      <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[4px] border-r-[4px] border-b-[5px] border-l-transparent border-r-transparent border-b-green-500" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    <p>Date de fin prévue : <strong>{dateFinPrevue ? format(new Date(dateFinPrevue), 'd MMM yyyy', { locale: fr }) : '—'}</strong></p>
                  </TooltipContent>
                </Tooltip>
              )}

              {/* Marqueur "Nouvelle fin (retard)" - orange - seulement si retard déclaré */}
              {calculations.nouvelleFinProgress !== null && joursRetard && joursRetard > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-15 cursor-help transition-all duration-500"
                      style={{ left: `${calculations.nouvelleFinProgress}%` }}
                    >
                      <div className="w-1.5 h-5 bg-orange-500 rounded-sm shadow-lg" />
                      <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[4px] border-r-[4px] border-b-[5px] border-l-transparent border-r-transparent border-b-orange-500" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    <p>Nouvelle fin (retard) : <strong>+{joursRetard}j</strong></p>
                  </TooltipContent>
                </Tooltip>
              )}

              {/* Marqueur "Aujourd'hui" */}
              <div
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 transition-all duration-500"
                style={{ left: `${calculations.progress}%` }}
              >
                <div className="w-2 h-4 bg-slate-200 rounded-sm shadow-md border border-slate-400/40" />
              </div>

              {/* Marqueur "Jour Critique" (fin de la barre) */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10">
                <div className="w-1.5 h-4 bg-red-500 rounded-sm shadow-lg animate-pulse" />
              </div>
            </div>

            {/* Graduations des jours */}
            <div className="relative h-6 mt-1">
              {/* Label début (Jour 0) */}
              <div className="absolute left-0 flex flex-col items-center -translate-x-1/2">
                <div className="w-px h-2 bg-slate-500/50" />
                <span className="text-[10px] text-slate-400 mt-0.5">0</span>
              </div>

              {/* Graduations intermédiaires */}
              {calculations.tickMarks.map((tick) => (
                <div
                  key={tick.day}
                  className="absolute flex flex-col items-center -translate-x-1/2"
                  style={{ left: `${tick.position}%` }}
                >
                  <div className="w-px h-2 bg-slate-500/50" />
                  {tick.showLabel && (
                    <span className="text-[10px] text-slate-400 mt-0.5">{tick.day}</span>
                  )}
                </div>
              ))}

              {/* Label fin (Jour critique) */}
              <div className="absolute right-0 flex flex-col items-center translate-x-1/2">
                <div className="w-px h-2 bg-red-500/50" />
                <span className="text-[10px] text-red-400 mt-0.5 font-medium">{calculations.jourCritiqueEffectif}</span>
              </div>
            </div>
          </div>
        </div>

          {/* Stats en dessous */}
          <div className="grid grid-cols-3 gap-4 mt-8 text-center">
            {/* Jours écoulés */}
            <div>
              <div className="text-3xl font-black text-foreground">
                {calculations.joursEffectifs}
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-sm text-foreground/60 mt-1 cursor-help inline-flex items-center gap-1">
                    jours écoulés
                    <Info className="w-3 h-3" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="text-sm">
                    Nombre de jours travaillés depuis le début du chantier.
                    Les jours de suspension sont exclus du calcul.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Jours de marge */}
            <div>
              <div className={`text-3xl font-black ${getMargeColor(calculations.joursDeMargeRestants)}`}>
                {calculations.joursDeMargeRestants ?? '—'}
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-sm text-foreground/60 mt-1 cursor-help inline-flex items-center gap-1">
                    jours de marge
                    <Info className="w-3 h-3" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="text-sm">
                    Jours restants avant d'atteindre le jour critique.
                    <br />
                    <span className="text-green-400">Vert</span> = confortable,{' '}
                    <span className="text-yellow-400">Jaune</span> = attention,{' '}
                    <span className="text-orange-400">Orange</span> = urgent,{' '}
                    <span className="text-red-400">Rouge</span> = critique.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Date jour critique */}
            <div>
              <div className="text-3xl font-black text-red-400">
                {formatDateShort(calculations.dateJourCritique)}
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-sm text-foreground/60 mt-1 cursor-help inline-flex items-center gap-1">
                    jour critique
                    <Info className="w-3 h-3" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="text-sm">
                    Date estimée à laquelle la marge du chantier atteindra 0€.
                    Basée sur les coûts équipe actuels et prévisionnels.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* Légende des marqueurs */}
          <div className="mt-6 pt-4 border-t border-border/20">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-foreground/70">
              <div className="flex items-center gap-2">
                <div className="w-2 h-4 bg-slate-200 rounded-sm border border-slate-400/40" />
                <span>Aujourd'hui</span>
              </div>
              {calculations.finPrevueProgress !== null && (
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-4 bg-green-500 rounded-sm" />
                  <span className="text-green-500">Fin prévue</span>
                </div>
              )}
              {joursRetard && joursRetard > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-4 bg-orange-500 rounded-sm" />
                  <span className="text-orange-500">Fin avec retard</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-4 bg-red-500 rounded-sm" />
                <span className="text-red-400">Jour critique</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}

export default CriticalDayVisualization;
